import * as React from 'react'
import { Pencil, Plus } from 'lucide-react'
import { useRouterState } from '@tanstack/react-router'
import { AppSidebar } from '@/components/sidebar'
import { BottomTabs } from '@/components/bottom-tabs'
import { AccountMenu } from '@/components/account-menu'
import { Button } from '@/components/ui/button'
import { IconTip } from '@/components/icon-tip'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { useNotes } from '@/state/notes-context'

const NoteEditor = React.lazy(() => import('@/components/note-editor').then((module) => ({ default: module.NoteEditor })))

export function AppShell({ children }: { children: React.ReactNode }) {
  const { createNote, notes, selectedNoteId, setSelectedNoteId, currentUser } = useNotes()
  const isMeals = useRouterState({ select: (state) => state.location.pathname === '/meals' })
  const [online, setOnline] = React.useState(navigator.onLine)

  React.useEffect(() => {
    const refresh = () => setOnline(navigator.onLine)
    window.addEventListener('online', refresh)
    window.addEventListener('offline', refresh)
    return () => { window.removeEventListener('online', refresh); window.removeEventListener('offline', refresh) }
  }, [])

  return (
    <SidebarProvider className="app-shell" style={{ '--sidebar-width': '15.5rem' } as React.CSSProperties}>
      {!isMeals && <AppSidebar />}
      <SidebarInset className="main">
        <div className="topbar-stack">
          {!online && <div className="offline-banner" role="status">You're offline. Changes will be saved locally and synced when you're next online.</div>}
          <header className={isMeals ? 'topbar topbar-meals' : 'topbar'}>
            {isMeals ? <div className="flex items-center gap-3 font-bold"><span className="brand-mark"><Pencil className="size-5" strokeWidth={2.4} /></span>Notes</div> : <>
              <SidebarTrigger className="mobile-menu" aria-label="Open menu" />
              <div className="flex items-center gap-3 font-bold"><span className="brand-mark"><Pencil className="size-5" strokeWidth={2.4} /></span>Notes</div>
            </>}
            <div className="top-actions"><AccountMenu user={currentUser} /></div>
          </header>
        </div>
        {children}
      </SidebarInset>
      {!isMeals && <IconTip label="New note">
        <Button className="fab" size="icon-lg" onClick={() => createNote()} aria-label="Create note"><Plus className="size-7" strokeWidth={2.4} /></Button>
      </IconTip>}
      <BottomTabs />
      <Dialog open={Boolean(selectedNoteId)} onOpenChange={(open) => !open && setSelectedNoteId(null)}>
        {selectedNoteId && (notes.some((note) => note.id === selectedNoteId)
          ? <React.Suspense fallback={<DialogContent><div className="min-h-72 animate-pulse rounded-2xl bg-[var(--hover)]" /></DialogContent>}><NoteEditor noteId={selectedNoteId} /></React.Suspense>
          : <DialogContent className="sm:max-w-md"><DialogTitle>Note unavailable</DialogTitle><DialogDescription>This note has not synced to this device, or you do not have access to it.</DialogDescription></DialogContent>)}
      </Dialog>
    </SidebarProvider>
  )
}
